'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import {
  isObservabilityLogsEnabled,
  redactString,
  setObservabilityLogsEnabled,
} from '@payments-checkout/shared/observability';
import { DatadogPlugin } from '@stone-payments/analitica/plugin-datadog';
import type { Metadata } from '@stone-payments/analitica';

type BasePluginConfig = ConstructorParameters<typeof DatadogPlugin>[0];

export type CheckoutDatadogPluginConfig = BasePluginConfig & {
  /**
   * Liga o SDK de Logs. Ligado por padrão: `addError` só é enviado em sessão
   * amostrada, então sem Logs perderíamos a maioria dos erros. O que era custo é
   * o `logger.notice` por evento do plugin base, que segue desligado.
   */
  logsEnabled?: boolean;
};

/** Tag `source` do padrão de tags da Stone. Mobile usa `android`. */
const SOURCE_TAG = 'web';

const isFlowControlError = (event: RumEvent) =>
  event.type === 'error' &&
  /^NEXT_(NOT_FOUND|REDIRECT)/.test(event.error?.message ?? '');

/**
 * Descarta o ruído de controle de fluxo do Next e redige PII. Só em evento de
 * erro: com `trackResources`, rodar regex em cada URL custaria CPU sem ganho.
 */
export function beforeSendRumEvent(event: RumEvent): boolean {
  if (isFlowControlError(event)) return false;

  if (event.type === 'error' && event.error) {
    if (event.error.message) {
      event.error.message = redactString(event.error.message);
    }
    if (event.error.stack) {
      event.error.stack = redactString(event.error.stack);
    }
  }

  return true;
}

/**
 * Contorna três limitações do `DatadogPlugin` do analitica (v3.1.0), que a
 * intenção é resolver via PR upstream:
 *
 * 1. `sendLogs` é all-or-nothing e o `datadogLogs.init` dele é hardcoded.
 * 2. `startSessionReplayRecording()` é chamado no construtor.
 * 3. `updateMetadata` usa `setGlobalContext`, que apaga o contexto inteiro.
 */
export class CheckoutDatadogPlugin extends DatadogPlugin {
  constructor({ logsEnabled = true, ...options }: CheckoutDatadogPluginConfig) {
    super({
      ...options,
      sendLogs: false,
      startSessionReplayRecordingManually: true,
    });

    // O super chama `startSessionReplayRecording()` mesmo em modo manual. Parar
    // aqui devolve o controle para `startSessionReplay()`, que só dispara depois
    // de o link ser validado. Não remover.
    datadogRum.stopSessionReplayRecording();

    // `reportError` consulta esta flag para decidir se escreve em Logs.
    setObservabilityLogsEnabled(logsEnabled);

    if (logsEnabled) {
      datadogLogs.init({
        clientToken: options.clientToken,
        site: options.site,
        service: options.service,
        env: options.env,
        version: options.version,
        // Safety net para o que a instrumentação explícita não alcança.
        forwardErrorsToLogs: true,
        // Estava em ['error','warn'] e capturava console.warn de terceiros.
        forwardConsoleLogs: [],
        beforeSend: (event) => {
          if (
            event.message &&
            /^NEXT_(NOT_FOUND|REDIRECT)/.test(event.message)
          ) {
            return false;
          }
          if (event.message) event.message = redactString(event.message);
          if (event.error?.stack) {
            event.error.stack = redactString(event.error.stack);
          }
          return true;
        },
      });

      // No SDK de Logs, `env` e `version` só chegam como ddtags — `@version:x`
      // não casaria com nada. Publicar como atributo iguala a query nos dois
      // canais (no RUM já vêm no corpo do evento).
      Object.entries({
        env: options.env,
        version: options.version,
        service: options.service,
        ...this.tags,
      }).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        datadogLogs.setGlobalContextProperty(key, value);
      });
    }

    // Só no RUM: o SDK de Logs de browser não tem API de tag arbitrária, e
    // `source` no corpo do log colidiria com o atributo reservado — mesmo motivo
    // pelo qual a origem do erro se chama `error_source`.
    datadogRum.setGlobalContextProperty('source', SOURCE_TAG);
  }

  /**
   * Aditivo, com `setGlobalContextProperty`: o plugin base usa
   * `setGlobalContext`, que apagava os atributos de correlação já publicados.
   */
  override updateMetadata(metadata: Metadata): void {
    const { user, ...rest } = metadata;

    if (user?.id) datadogRum.setUser({ id: user.id });

    const flat = { ...this.tags, ...rest } as Record<string, unknown>;
    const logsEnabled = isObservabilityLogsEnabled();

    Object.entries(flat).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      datadogRum.setGlobalContextProperty(key, value);
      // Espelhado nos Logs, que é o canal com cobertura total.
      if (logsEnabled) {
        datadogLogs.setGlobalContextProperty(key, value);
      }
    });
  }
}

